import React, {useContext, useMemo} from 'react';
import {MutateServiceRequest, space} from "../../../../../provider_api";
import {useProviderProfile} from "../../../../../ProviderProfileProvider";
import {useParams} from "react-router-dom";
import {EditableStringField} from "../../../../../components/Editable/EditableStringField";
import {EditableLongTextField} from "../../../../../components/Editable/EditableLongTextField";
import {
  EditableStringFieldWithSuggestions
} from "../../../../../components/Editable/EditableStringFieldWithSuggestions";
import {EditableMultiSelect} from "../../../../../components/Editable/EditableMultiSelect";
import {findService} from "../../Calendar/CalendarUtil";
import {getPractitionerName} from "../../../../../util/ProviderUtil";
import {KensuProfileEditor} from "../../../../../components/table/KensuProfileEditor";
import {EditablePaymentPolicy} from "./EditablePaymentPolicy";
import ServiceProto = space.kenko.proto.ServiceProto;
import IServiceProto = space.kenko.proto.IServiceProto;
import {EditableYesNo} from "../../../../../components/Editable/EditableYesNo";
import {EditableContext} from "../../../../../components/Editable/EditableContext";

// This is the page for editing (or adding) a service.
export const AddOrUpdateService = () => {

  // If editing, the sku is a param. 
  const {idParam} = useParams();
  const {business, businessProfile} = useProviderProfile();

  const existingCategories = useMemo(() => {
    const categories: string[] = [];
    businessProfile?.practice?.services?.forEach(service => {
      if (service.category && categories.indexOf(service.category) < 0) {
        categories.push(service.category);
      }
    });
    return categories;
  }, [businessProfile])


  return <KensuProfileEditor<IServiceProto>
      idParam={idParam}
      entityName={'Service'}
      entityListViewLink={'../services'}
      archiveField={"archived"}
      getFromProfile={(profile, id) => findService(profile, id)}
      getTitle={service => service.name}
      newBlankType={() => new ServiceProto({
        paymentPolicy: {}
      })}
      getRpcMethod={isUpdate => isUpdate ? 'update_service' : 'create_service'}
      getRpcPayload={service => MutateServiceRequest.encode(new MutateServiceRequest({
        service: service
      })).finish()}
  >

    <EditableStringField<IServiceProto>
        label={'Name'}
        inputType={'text'}
        field={'name'}
    />

    <EditableLongTextField<IServiceProto>
        label={'Description'}
        field={'description'}
    />

    <EditableStringFieldWithSuggestions<IServiceProto>
        label={'Category'}
        field={'category'}
        suggestions={existingCategories}
        helpText={'Your services will be categorized by this value in the booking flow.'}
    />

    <EditableStringField<IServiceProto>
        label={'Length (minutes)'}
        inputType={'number'}
        field={'lengthMinutes'}
    />

    <EditablePaymentPolicy<IServiceProto>
        label={'Payment Policy'}
        profile={businessProfile}
        field={'paymentPolicy'}
    />

    {business?.businessSettings?.offersVirtualServices &&
        <EditableYesNo<IServiceProto>
            label={'Is a virtual service'}
            field={'isVirtual'}
            getter={entity => entity.isVirtual}
            setter={(entity, value) => ({
                  ...entity,
                  availableLocationIds: [],
                  isVirtual: value
                }
            )}
        />}

    <EditableMultiSelect<IServiceProto, string>
        label={'Locations'}
        errorKey={'locations'}
        field={'availableLocationIds'}
        helpText={'The locations at which this service will be offered.'}
        visibleWhen={service => !service.isVirtual}
        options={businessProfile?.locations?.map(loc => {
          return {
            value: loc.locationId as string,
            label: loc.locationName as string
          }
        }) ?? []}/>

    <EditableMultiSelect<IServiceProto, string>
        label={'Team Members'}
        field={'practitionerId'}
        errorKey={'practitioners'}
        helpText={'The team members who offer this service.'}
        options={businessProfile?.practitioners?.filter(p => !p.archived).map(p => {
          return {
            value: p.practitionerId as string,
            label: getPractitionerName(p)
          }
        }) ?? []}/>

  </KensuProfileEditor>
};

